import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

/**
 * Slice B.2 — Glasgow Coma Scale renderer.
 * Emits the B.1 backend contract: { eye, verbal, motor, total }.
 * Total only emitted once all three components are chosen.
 */

function severity(total) {
  if (total == null) return null;
  if (total >= 13) return { label: 'Mild', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
  if (total >= 9) return { label: 'Moderate', cls: 'bg-amber-100 text-amber-700 border-amber-300' };
  return { label: 'Severe', cls: 'bg-red-100 text-red-700 border-red-300' };
}

const COMPONENTS = [
  { key: 'eye', label: 'Eye', max: 4, testId: 'chart-field-gcs-eye' },
  { key: 'verbal', label: 'Verbal', max: 5, testId: 'chart-field-gcs-verbal' },
  { key: 'motor', label: 'Motor', max: 6, testId: 'chart-field-gcs-motor' },
];

export function GcsScoreField({ value, onChange }) {
  const v = value && typeof value === 'object' ? value : {};

  const update = (k, n) => {
    const next = { eye: v.eye, verbal: v.verbal, motor: v.motor, [k]: n };
    const complete = next.eye != null && next.verbal != null && next.motor != null;
    onChange({
      ...next,
      total: complete ? next.eye + next.verbal + next.motor : null,
    });
  };

  const total = (v.eye != null && v.verbal != null && v.motor != null)
    ? v.eye + v.verbal + v.motor
    : null;
  const sev = severity(total);

  return (
    <div className="rounded-md border p-3 space-y-3" data-testid="chart-field-gcs">
      <div className="grid grid-cols-3 gap-2">
        {COMPONENTS.map(c => (
          <div key={c.key}>
            <Label className="text-[11px] text-muted-foreground">{c.label} (1–{c.max})</Label>
            <Select
              value={v[c.key] != null ? String(v[c.key]) : undefined}
              onValueChange={n => update(c.key, Number(n))}
            >
              <SelectTrigger className="mt-1 h-9" data-testid={c.testId}>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: c.max }, (_, i) => i + 1).map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Total (auto)</span>
        <span
          data-testid="chart-field-gcs-total"
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold ${sev ? sev.cls : 'bg-slate-100 text-slate-500 border-slate-200'}`}
        >
          {total != null ? `${total} / 15` : '— / 15'}
          {sev ? <span className="text-[11px] font-medium">· {sev.label}</span> : null}
        </span>
      </div>
    </div>
  );
}

export default GcsScoreField;
